"use client";

import { useMemo } from "react";
import { TrendingUp } from "lucide-react";
import DashboardCard from "./DashboardCard";
import DashboardCardLoading from "./DashboardCardLoading";
import DailyAccuracyChart from "@/components/dashboard/DailyAccuracyChart";
import {
  computeErrorNoteDailyStats,
  type ErrorNoteDailyStats,
} from "@/lib/error-note-stats";
import type { ProblemHistory } from "@/lib/types";

/**
 * 대시보드 "오답노트 정답률" 위젯 (JCG-2073).
 * 풀이 이력(페이지에서 1회 fetch → prop)으로 일자별 정답률 추세를 보여준다. 카드 제목은
 * 차트(DailyAccuracyChart)가 자체 보유.
 *
 * 지난 회차 스냅샷(JCG-3283)은 histories 대신 미리 계산된 `dailyStats`를 주입한다.
 */
export default function DashboardAccuracy({
  histories,
  loading = false,
  dailyStats: injectedStats,
}: {
  histories?: ProblemHistory[];
  loading?: boolean;
  dailyStats?: ErrorNoteDailyStats[];
}) {
  const dailyStats = useMemo(
    () => injectedStats ?? computeErrorNoteDailyStats(histories ?? []),
    [histories, injectedStats],
  );

  return (
    <DashboardCard
      icon={TrendingUp}
      title="일자별 정답률 변화"
      href="/my/error-notes"
      linkLabel="오답노트"
    >
      {loading ? (
        <DashboardCardLoading height={220} label="정답률 불러오는 중…" />
      ) : dailyStats.length > 1 ? (
        <DailyAccuracyChart dailyStats={dailyStats} showTitle={false} />
      ) : (
        <div className="flex h-[220px] flex-col items-center justify-center gap-2 text-center">
          <TrendingUp className="h-7 w-7 text-muted-foreground/60" aria-hidden />
          <p className="text-sm text-muted-foreground">
            문제를 풀면 일자별 정답률 변화가 여기에 표시돼요.
          </p>
        </div>
      )}
    </DashboardCard>
  );
}
